<template>
  <aside
    class="sidebar"
    :class="{
      'sidebar-dark': isDarkMode,
      'sidebar-collapsed': collapsed,
      'sidebar-flyout-open': !!activeFlyout || profileMenuOpen
    }"
  >
    <div class="sidebar-header">
      <div class="sidebar-header-brand-slot">
        <div class="sidebar-collapse-hit-area d-none d-md-flex">
          <button
            type="button"
            class="sidebar-collapse-btn"
            :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
            @click="toggleCollapsed"
          >
            <img
              :src="sidebarOpenIcon"
              alt=""
              class="sidebar-collapse-icon"
              aria-hidden="true"
            />
          </button>
        </div>
        <router-link class="sidebar-brand" :to="navbarBrandRoute" @click="closeFlyout">
          <img :src="spencersLogo" alt="Spencer's" class="sidebar-brand-spencers" />
          <img :src="natureBasketLogo" alt="Nature's Basket" class="sidebar-brand-nature-basket" />
        </router-link>
      </div>
    </div>

    <div class="sidebar-body">
      <nav class="sidebar-nav flex-column nav" aria-label="Main navigation">
      <template v-if="isQmsOtpGuestSession">
        <router-link
          :to="{ path: '/qms' }"
          class="sidebar-item nav-link"
          active-class="is-active"
          @click="closeFlyout"
        >
          <font-awesome-icon :icon="['fas', 'clipboard-list']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">QMS</span>
        </router-link>
        <router-link
          :to="{ path: '/qms/new' }"
          class="sidebar-item nav-link"
          active-class="is-active"
          @click="closeFlyout"
        >
          <font-awesome-icon :icon="['fas', 'list-alt']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">New query</span>
        </router-link>
      </template>
      <template v-else>
      <router-link
        v-if="qmsNavBarItemPermission"
        :to="{ path: '/qms' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'clipboard-list']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">QMS</span>
      </router-link>
      <router-link
        v-if="qmsNavBarItemPermission"
        :to="{ path: '/qms/admin' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'clipboard-list']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">QMS Admin</span>
      </router-link>
      <router-link
        v-if="qmsNavBarItemPermission"
        :to="{ path: '/qms/closure-remarks' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'list-alt']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Closure Remarks</span>
      </router-link>

      <router-link
        v-if="qmsEscalationMatrixNavBarItemPermission"
        :to="{ path: '/qms/escalation-matrix' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'sitemap']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Escalation Matrix</span>
      </router-link>
      <router-link
        v-if="dropdownsAdminNavBarItemPermission"
        :to="{ path: '/dropdowns-admin' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'list']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Dropdowns Admin</span>
      </router-link>
      <router-link
        v-if="dashboardNavBarItemPermission"
        :to="{ path: '/' }"
        class="sidebar-item nav-link"
        exact-active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'home']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Dashboard</span>
      </router-link>

      <router-link
        v-if="reportNavBarItemPermission || specialReportNavBarItemPermission"
        :to="{ path: '/report' }"
        class="sidebar-item nav-link"
        :class="{ 'is-active': isReportsRouteActive }"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'book']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Reports</span>
      </router-link>

      <!-- Checklist-->
      <div
        v-if="checklistNavBarItemPermission"
        class="sidebar-group sidebar-group-flyout"
        data-sidebar-group="checklist"
      >
        <button
          type="button"
          class="sidebar-item sidebar-item-toggle"
          :class="{ 'is-active': activeFlyout === 'checklist' || isChecklistRouteActive }"
          @click="toggleFlyout('checklist')"
        >
          <font-awesome-icon :icon="['fas', 'check']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">Checklist</span>
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            class="sidebar-chevron sidebar-chevron-right"
            :class="{ 'is-open': isCompactSidebar && activeFlyout === 'checklist' }"
            aria-hidden="true"
          />
        </button>
        <nav
          v-if="isCompactSidebar && activeFlyout === 'checklist' && checklistItems.length"
          class="sidebar-submenu"
          aria-label="Checklist"
        >
          <router-link
            v-for="item in checklistItems"
            :key="item.path"
            :to="{ path: item.path }"
            class="sidebar-subitem nav-link"
            active-class="is-active"
            @click="closeFlyout"
          >
            <font-awesome-icon :icon="['fas', item.icon]" class="sidebar-item-icon" />
            <span class="sidebar-item-label">{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <router-link
        v-if="dynamicFormNavBarPermission"
        :to="{ path: '/form' }"
        class="sidebar-item nav-link"
        :class="{ 'is-active': isFormsRouteActive }"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'list-alt']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Dynamic Forms</span>
      </router-link>

      <router-link
        v-if="eventNavBarItemPermission"
        :to="{ path: '/photo-sharing-event' }"
        class="sidebar-item nav-link"
        :class="{ 'is-active': isEventsRouteActive }"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['far', 'calendar']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Events</span>
      </router-link>

      <!-- NPS-->
      <div
        v-if="npsNavBarItemPermission"
        class="sidebar-group sidebar-group-flyout"
        data-sidebar-group="nps"
      >
        <button
          type="button"
          class="sidebar-item sidebar-item-toggle"
          :class="{ 'is-active': activeFlyout === 'nps' || isNpsRouteActive }"
          @click="toggleFlyout('nps')"
        >
          <font-awesome-icon :icon="['fas', 'scroll']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">NPS</span>
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            class="sidebar-chevron sidebar-chevron-right"
            :class="{ 'is-open': isCompactSidebar && activeFlyout === 'nps' }"
            aria-hidden="true"
          />
        </button>
        <nav
          v-if="isCompactSidebar && activeFlyout === 'nps' && npsItems.length"
          class="sidebar-submenu"
          aria-label="NPS"
        >
          <router-link
            v-for="item in npsItems"
            :key="item.path"
            :to="{ path: item.path }"
            class="sidebar-subitem nav-link"
            active-class="is-active"
            @click="closeFlyout"
          >
            <font-awesome-icon
              v-if="item.faIcon"
              :icon="item.faIcon"
              :class="['sidebar-item-icon', item.iconImageClass]"
              aria-hidden="true"
            />
            <font-awesome-icon v-else :icon="['fas', item.icon]" class="sidebar-item-icon" />
            <span class="sidebar-item-label">{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <!-- More Functions + flyout-->
      <div
        v-if="moreFunctionNavBarItemPermission"
        class="sidebar-group sidebar-group-flyout"
        data-sidebar-group="more-functions"
      >
        <button
          type="button"
          class="sidebar-item sidebar-item-toggle"
          :class="{ 'is-active': activeFlyout === 'more-functions' || isMoreFunctionsRouteActive }"
          @click="toggleFlyout('more-functions')"
        >
          <font-awesome-icon :icon="['fas', 'border-all']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">More Functions</span>
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            class="sidebar-chevron sidebar-chevron-right"
            :class="{ 'is-open': isCompactSidebar && activeFlyout === 'more-functions' }"
            aria-hidden="true"
          />
        </button>
        <nav
          v-if="isCompactSidebar && activeFlyout === 'more-functions' && moreFunctionItems.length"
          class="sidebar-submenu sidebar-submenu-more-functions"
          aria-label="More functions"
        >
          <router-link
            v-for="item in moreFunctionItems"
            :key="item.path"
            :to="{ path: item.path }"
            class="sidebar-subitem nav-link"
            active-class="is-active"
            @click="closeFlyout"
          >
            <font-awesome-icon
              v-if="item.faIcon"
              :icon="item.faIcon"
              :class="['sidebar-item-icon', item.iconImageClass]"
              aria-hidden="true"
            />
            <font-awesome-icon v-else :icon="['fas', item.icon]" class="sidebar-item-icon" />
            <span class="sidebar-item-label">{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <!-- Staff-->
      <div
        v-if="staffNavBarItemPermission"
        class="sidebar-group sidebar-group-flyout"
        data-sidebar-group="staff"
      >
        <button
          type="button"
          class="sidebar-item sidebar-item-toggle"
          :class="{ 'is-active': activeFlyout === 'staff' || isStaffRouteActive }"
          @click="toggleFlyout('staff')"
        >
          <font-awesome-icon :icon="['fas', 'users']" class="sidebar-item-icon" aria-hidden="true" />
          <span class="sidebar-item-label">Staff</span>
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            class="sidebar-chevron sidebar-chevron-right"
            :class="{ 'is-open': isCompactSidebar && activeFlyout === 'staff' }"
            aria-hidden="true"
          />
        </button>
        <nav
          v-if="isCompactSidebar && activeFlyout === 'staff' && staffItems.length"
          class="sidebar-submenu"
          aria-label="Staff"
        >
          <router-link
            v-for="item in staffItems"
            :key="item.path"
            :to="{ path: item.path }"
            class="sidebar-subitem nav-link"
            active-class="is-active"
            @click="closeFlyout"
          >
            <font-awesome-icon :icon="['fas', item.icon]" class="sidebar-item-icon" />
            <span class="sidebar-item-label">{{ item.label }}</span>
          </router-link>
        </nav>
      </div>

      <router-link
        v-if="pettyCashNavBarItemPermission"
        :to="{ path: '/petty-cash' }"
        class="sidebar-item nav-link"
        :class="{ 'is-active': isPettyCashRouteActive }"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'indian-rupee-sign']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Petty Cash</span>
      </router-link>

     <router-link
        v-if="chatPermission"
        :to="{ path: '/chat' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'comments']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">Chat</span>
      </router-link>

      <!--<router-link
        v-if="chatPermission"
        :to="{ path: '/disha-chat' }"
        class="sidebar-item nav-link"
        active-class="is-active"
        @click="closeFlyout"
      >
        <font-awesome-icon :icon="['fas', 'comments']" class="sidebar-item-icon" aria-hidden="true" />
        <span class="sidebar-item-label">DishaChat</span>
      </router-link>-->
      </template>
      </nav>
    </div>

    <div class="sidebar-footer" data-sidebar-group="profile">
      <button
        type="button"
        class="sidebar-profile-trigger-btn"
        :class="{ 'is-active': profileMenuOpen }"
        @click="toggleProfileMenu"
      >
        <span class="sidebar-avatar">{{ userInitials }}</span>
        <span class="sidebar-footer-text">
          <strong>{{ user && user.name ? user.name : 'Admin' }}</strong>
          <small>{{ userRoleLabel }}</small>
        </span>
        <span class="sidebar-footer-chevron-wrap" aria-hidden="true">
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="sidebar-footer-chevron" />
        </span>
      </button>
    </div>

    <!-- Nav flyout panel (desktop / tablet md+) -->
    <transition name="sidebar-flyout">
      <div
        ref="navFlyout"
        v-if="!isCompactSidebar && activeFlyout && activeFlyoutItems.length"
        class="sidebar-flyout"
        :class="{ 'sidebar-flyout-dark': isDarkMode }"
        :style="flyoutStyle"
        @mouseleave="onFlyoutMouseLeave"
      >
        <nav class="sidebar-flyout-nav" :aria-label="flyoutAriaLabel">
          <router-link
            v-for="item in activeFlyoutItems"
            :key="item.path"
            :to="{ path: item.path }"
            class="sidebar-flyout-item nav-link"
            active-class="is-active"
            @click="closeFlyout"
          >
            <font-awesome-icon
              v-if="item.faIcon"
              :icon="item.faIcon"
              :class="['sidebar-item-icon', item.iconImageClass]"
              aria-hidden="true"
            />
            <font-awesome-icon v-else :icon="['fas', item.icon]" class="sidebar-item-icon" />
            <span class="sidebar-item-label">{{ item.label }}</span>
          </router-link>
        </nav>
      </div>
    </transition>

    <!-- Profile flyout panel-->
    <transition name="sidebar-flyout">
      <div
        v-if="profileMenuOpen"
        class="sidebar-flyout sidebar-profile-flyout"
        :class="{ 'sidebar-flyout-dark': isDarkMode }"
        :style="profileFlyoutStyle"
      >
        <nav class="sidebar-flyout-nav" aria-label="Profile menu">
          <template v-for="item in profileMenuItems">
            <a
              v-if="item.href"
              :key="item.key"
              :href="item.href"
              target="_blank"
              rel="noopener noreferrer"
              class="sidebar-flyout-item nav-link"
              @click="closeFlyout"
            >
              <span class="sidebar-item-icon" aria-hidden="true">
                <font-awesome-icon :icon="['fas', item.icon]" />
              </span>
              <span class="sidebar-item-label">{{ item.label }}</span>
            </a>
            <router-link
              v-else
              :key="item.key"
              :to="item.to"
              class="sidebar-flyout-item nav-link"
              active-class="is-active"
              @click="closeFlyout"
            >
              <span class="sidebar-item-icon" aria-hidden="true">
                <font-awesome-icon :icon="['fas', item.icon]" />
              </span>
              <span class="sidebar-item-label">{{ item.label }}</span>
            </router-link>
          </template>
        </nav>
      </div>
    </transition>
  </aside>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { Permissions } from '@/helper/navBarHelper';
import { isDishaChatEnabled } from '@/helper/dishaChatMessageUtils';
import { hasDishaAiAccess } from '@/helper/dishaAiAccess';
import configService from '@/services/configService';
import natureBasketLogo from '@/assets/images/NatureBasketLogo.png';
import spencersLogo from '@/assets/images/spencers.png';
import sidebarOpenIcon from '@/assets/images/open.png';

const createDefaultNavPermissions = () =>
  [
    'specialReportNavBarItemPermission',
    'dashboardNavBarItemPermission',
    'reportNavBarItemPermission',
    'checklistNavBarItemPermission',
    'dynamicFormNavBarPermission',
    'eventNavBarItemPermission',
    'npsNavBarItemPermission',
    'moreFunctionNavBarItemPermission',
    'staffNavBarItemPermission',
    'pettyCashNavBarItemPermission',
    'reportPermission',
    'foodSafetyPermission',
    'viewChecklistPermission',
    'viewFoodSafetyChecklistPermission',
    'fillChecklistPermission',
    'npsVenderPermission',
    'npsCustomerPermission',
    'supportTicketsPermission',
    'popPermission',
    'tms',
    'ticketsPermission',
    'transactionComplainPermission',
    'eneTracker',
    'rtv',
    'osaReportPermission',
    'kycPermission',
    'kycInstitutionalPermission',
    'storeLicensedPermission',
    'promoCodePermission',
    'giftIssuancePermission',
    'qmsPermission',
    'qmsNavBarItemPermission',
    'qmsEscalationMatrixNavBarItemPermission',
    'dropdownsAdminNavBarItemPermission',
    'commentsPermission',
    'notesPermission',
    'schedulesPermission',
    'tagsPermission',
    'storeDocumentPermission',
    'videosPermission',
    'userPermission',
    'rolesPermission',
    'storesPermission',
    'chatPermission',
    'dishaChatPermission'
  ].reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

const STAFF_PATHS = ['/user', '/role', '/stores'];
const NPS_PATHS = ['/vendorFeedbackReport', '/nps'];
const EVENT_PATHS = [
  '/photo-sharing-event',
  '/eventplanlist',
  '/event-plan',
  '/active-event-list'
];
const FORM_PATHS = ['/form'];
const REPORT_PATHS = ['/report', '/checklist_report'];
const PETTY_CASH_PATHS = ['/petty-cash'];
const COMPACT_SIDEBAR_MEDIA = '(max-width: 767.98px)';

const MORE_FUNCTION_PATHS = [
  '/qms',
  '/qms/escalation-matrix',
  '/dropdowns-admin',
  '/complain',
  '/pop',
  '/cx-pulse-360',
  '/kyc',
  '/transaction-complain',
  '/eneTracker',
  '/rtv',
  '/barcodeScan',
  '/kycInstitutional',
  '/license-upload',
  '/promo-code',
  '/gift-distribute-app',
  '/comments',
  '/notes',
  '/schedule',
  '/tags',
  '/document-share',
  '/video'
];

export default {
  name: 'AppSidebar',
  data() {
    return {
      ...createDefaultNavPermissions(),
      collapsed: false,
      activeFlyout: null,
      flyoutTop: 72,
      profileMenuOpen: false,
      profileFlyoutLeft: 208,
      profileFlyoutBottom: 16,
      isCompactSidebar: false,
      compactSidebarMql: null,
      natureBasketLogo,
      spencersLogo,
      sidebarOpenIcon
    };
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('darkMode', ['isDarkMode']),
    ...mapState('cstSubmission', ['store']),
    ...mapGetters('auth', ['isLoggedIn', 'isQmsAdmin']),
    navbarBrandRoute() {
      if (this.isQmsOtpGuestSession || this.isQmsAdmin) {
        return { path: '/qms' };
      }
      return { path: '/' };
    },
    isQmsOtpGuestSession() {
      if (typeof sessionStorage === 'undefined') {
        return false;
      }
      return sessionStorage.getItem('qmsOtpVerified') === 'true' && !this.isLoggedIn();
    },
    userInitials() {
      let name = 'AD';
      if (this.user && this.user.name) {
        name = this.user.name;
      } else if (this.user && this.user.mobile) {
        name = this.user.mobile;
      }
      return String(name)
        .split(' ')
        .map(item => item.charAt(0))
        .join('')
        .substring(0, 2)
        .toUpperCase();
    },
    userRoleLabel() {
      if (!this.user || !this.user.roles || !this.user.roles[0]) {
        return 'User';
      }
      const role = this.user.roles[0].name || 'User';
      return role
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    profileMenuItems() {
      return [
        {
          key: 'yvm',
          label: 'Go To YVM',
          href: 'https://yvm.spencersretail.com',
          icon: 'check',
          visible: !this.isQmsOtpGuestSession
        },
        {
          key: 'user-manual',
          label: 'User Manual',
          href: 'https://spencer-doc-share.s3.ap-south-1.amazonaws.com/CRM+V2+ENG.pdf',
          icon: 'question-circle',
          visible: !this.isQmsOtpGuestSession
        },
        {
          key: 'logout',
          label: 'Logout',
          to: { path: '/logout' },
          icon: 'sign-out-alt',
          visible: true
        }
      ].filter(item => item.visible);
    },
    checklistItems() {
      return [
        { label: 'View Checklist', path: '/checklist', icon: 'eye', visible: this.viewChecklistPermission },
        { label: 'Fill Checklist', path: '/checklist-fill', icon: 'edit', visible: this.fillChecklistPermission }
      ].filter(item => item.visible);
    },
    npsItems() {
      return [
        {
          label: 'NPS Vendor',
          path: '/vendorFeedbackReport',
          faIcon: ['fas', 'shopping-bag'],
          visible: this.npsVenderPermission
        },
        {
          label: 'Voice Of Customer',
          path: '/nps',
          faIcon: ['fas', 'user'],
          iconImageClass: 'sidebar-flyout-item-icon-nps-customer',
          visible: this.npsCustomerPermission
        }
      ].filter(item => item.visible);
    },
    staffItems() {
      return [
        { label: 'Users', path: '/user', icon: 'user-tie', visible: this.userPermission },
        { label: 'Roles', path: '/role', icon: 'at', visible: this.rolesPermission },
        { label: 'Stores', path: '/stores', icon: 'store', visible: this.storesPermission }
      ].filter(item => item.visible);
    },
    moreFunctionItems() {
      return [
        { label: 'QMS', path: '/qms', icon: 'clipboard-list', visible: this.qmsPermission },
        {
          label: 'Escalation Matrix',
          path: '/qms/escalation-matrix',
          icon: 'sitemap',
          visible: this.qmsEscalationMatrixNavBarItemPermission
        },
        {
          label: 'Dropdowns Admin',
          path: '/dropdowns-admin',
          icon: 'list',
          visible: this.dropdownsAdminNavBarItemPermission
        },
        { label: 'Tickets', path: '/complain', icon: 'ticket-alt', visible: this.supportTicketsPermission },
        { label: 'POP', path: '/pop', icon: 'print', visible: this.popPermission },
        { label: 'CX Pulse 360', path: '/cx-pulse-360', icon: 'receipt', visible: this.tms },
        { label: 'Sampark', path: '/kyc', icon: 'handshake', visible: this.kycPermission },
        {
          label: 'Transaction Complain',
          path: '/transaction-complain',
          icon: 'exchange-alt',
          visible: this.transactionComplainPermission
        },
        { label: 'EnE Tracker', path: '/eneTracker', icon: 'chart-line', visible: this.eneTracker },
        { label: 'RTV', path: '/rtv', icon: 'undo', visible: this.rtv },
        { label: 'OSA Report', path: '/barcodeScan', icon: 'barcode', visible: this.osaReportPermission },
        {
          label: 'KYC Institutional',
          path: '/kycInstitutional',
          icon: 'fingerprint',
          visible: this.kycInstitutionalPermission
        },
        { label: 'Store Licenses', path: '/license-upload', icon: 'id-card', visible: this.storeLicensedPermission },
        { label: 'Promo Code', path: '/promo-code', icon: 'ad', visible: this.promoCodePermission },
        {
          label: 'Gift Issuance',
          path: '/gift-distribute-app',
          icon: 'hand-holding-usd',
          visible: this.giftIssuancePermission
        },
        { label: 'Comments', path: '/comments', icon: 'comments', visible: this.commentsPermission },
        { label: 'Notes', path: '/notes', icon: 'pen', visible: this.notesPermission },
        { label: 'Schedules', path: '/schedule', icon: 'calendar-check', visible: this.schedulesPermission },
        { label: 'Tags', path: '/tags', icon: 'tags', visible: this.tagsPermission },
        {
          label: 'Store Documents',
          path: '/document-share',
          icon: 'clipboard-list',
          visible: this.storeDocumentPermission
        },
        { label: 'Videos', path: '/video', icon: 'play', visible: this.videosPermission }
      ].filter(item => item.visible);
    },
    isChecklistRouteActive() {
      return this.checklistItems.some(item => this.isRouteActive(item.path));
    },
    isNpsRouteActive() {
      return NPS_PATHS.some(path => this.isRouteActive(path));
    },
    isStaffRouteActive() {
      return STAFF_PATHS.some(path => this.isRouteActive(path));
    },
    isMoreFunctionsRouteActive() {
      return MORE_FUNCTION_PATHS.some(path => this.isRouteActive(path));
    },
    isEventsRouteActive() {
      return EVENT_PATHS.some(path => this.isRouteActive(path));
    },
    isFormsRouteActive() {
      return FORM_PATHS.some(path => this.isRouteActive(path));
    },
    isReportsRouteActive() {
      return REPORT_PATHS.some(path => this.isRouteActive(path));
    },
    isPettyCashRouteActive() {
      return PETTY_CASH_PATHS.some(path => this.isRouteActive(path));
    },
    activeFlyoutItems() {
      switch (this.activeFlyout) {
        case 'checklist':
          return this.checklistItems;
        case 'nps':
          return this.npsItems;
        case 'staff':
          return this.staffItems;
        case 'more-functions':
          return this.moreFunctionItems;
        default:
          return [];
      }
    },
    flyoutAriaLabel() {
      const labels = {
        checklist: 'Checklist',
        nps: 'NPS',
        staff: 'Staff',
        'more-functions': 'More functions'
      };
      return labels[this.activeFlyout] || 'Submenu';
    },
    flyoutStyle() {
      return {
        top: `${this.flyoutTop}px`
      };
    },
    profileFlyoutStyle() {
      return {
        top: 'auto',
        bottom: `${this.profileFlyoutBottom}px`,
        left: `${this.profileFlyoutLeft}px`
      };
    }
  },
  watch: {
    '$route.path': {
      handler() {
        this.activeFlyout = null;
        this.profileMenuOpen = false;
      },
      immediate: true
    },
    collapsed() {
      this.$nextTick(() => {
        if (this.profileMenuOpen) {
          this.updateProfileFlyoutPosition();
        }
        if (this.activeFlyout) {
          this.updateFlyoutPosition(this.activeFlyout);
        }
      });
    }
  },
  mounted() {
    if (!this.isQmsOtpGuestSession) {
      this.getStoreByUser();
      this.handlePermissions();
    }
    this.initCompactSidebarWatcher();
    document.addEventListener('click', this.onDocumentClick);
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeUnmount() {
    this.destroyCompactSidebarWatcher();
    document.removeEventListener('click', this.onDocumentClick);
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    ...mapActions('cstSubmission', ['getStoreByUser']),
    initCompactSidebarWatcher() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return;
      }
      this.compactSidebarMql = window.matchMedia(COMPACT_SIDEBAR_MEDIA);
      this.syncCompactSidebar(this.compactSidebarMql);
      if (typeof this.compactSidebarMql.addEventListener === 'function') {
        this.compactSidebarMql.addEventListener('change', this.onCompactSidebarChange);
      } else if (typeof this.compactSidebarMql.addListener === 'function') {
        this.compactSidebarMql.addListener(this.onCompactSidebarChange);
      }
    },
    destroyCompactSidebarWatcher() {
      if (!this.compactSidebarMql) {
        return;
      }
      if (typeof this.compactSidebarMql.removeEventListener === 'function') {
        this.compactSidebarMql.removeEventListener('change', this.onCompactSidebarChange);
      } else if (typeof this.compactSidebarMql.removeListener === 'function') {
        this.compactSidebarMql.removeListener(this.onCompactSidebarChange);
      }
      this.compactSidebarMql = null;
    },
    onCompactSidebarChange(event) {
      this.syncCompactSidebar(event);
    },
    syncCompactSidebar(mql) {
      const nextValue = !!(mql && mql.matches);
      const wasCompact = this.isCompactSidebar;
      this.isCompactSidebar = nextValue;
      if (!nextValue && wasCompact && this.activeFlyout) {
        this.$nextTick(() => this.updateFlyoutPosition(this.activeFlyout));
      }
    },
    toggleCollapsed() {
      this.collapsed = !this.collapsed;
      this.$emit('collapse-change', this.collapsed);
    },
    handlePermissions() {
      const userPermissions = Permissions(this.user, this.store);
      if (userPermissions) {
        userPermissions.forEach(permission => {
          if (Object.prototype.hasOwnProperty.call(this.$data, permission)) {
            this.$data[permission] = true;
          }
        });
      } else {
        const defaultUser = [
          'reportNavBarItemPermission',
          'checklistNavBarItemPermission',
          'fillChecklistPermission',
          'dynamicFormNavBarPermission',
          'eventNavBarItemPermission',
          'moreFunctionNavBarItemPermission',
          'supportTicketsPermission',
          'osaReportPermission',
          'kycPermission',
          'commentsPermission',
          'notesPermission'
        ];
        defaultUser.forEach(permission => {
          if (Object.prototype.hasOwnProperty.call(this.$data, permission)) {
            this.$data[permission] = true;
          }
        });
      }
      if (hasDishaAiAccess(this.user) && Object.prototype.hasOwnProperty.call(this.$data, 'chatPermission')) {
        this.$data.chatPermission = true;
      }
      if (Object.prototype.hasOwnProperty.call(this.$data, 'dishaChatPermission')) {
        this.$data.dishaChatPermission = isDishaChatEnabled(
          this.user,
          configService.get('enableDishaChat') === true
        );
      }
    },
    isRouteActive(path) {
      if (path === '/') {
        return this.$route.path === '/';
      }
      return this.$route.path === path || this.$route.path.startsWith(`${path}/`);
    },
    toggleFlyout(group) {
      if (this.activeFlyout === group) {
        this.activeFlyout = null;
        return;
      }

      this.profileMenuOpen = false;
      this.activeFlyout = group;
      this.$nextTick(() => {
        if (!this.isCompactSidebar) {
          this.updateFlyoutPosition(group);
        }
        this.scrollGroupIntoView(group);
      });
    },
    updateFlyoutPosition(group, allowRetry = true) {
      if (this.isCompactSidebar) {
        return;
      }
      const groupEl = this.$el.querySelector(`[data-sidebar-group="${group}"] .sidebar-item-toggle`);
      if (!groupEl) {
        this.flyoutTop = 72;
        return;
      }

      const rect = groupEl.getBoundingClientRect();
      const triggerCenterY = rect.top + rect.height / 2;
      const flyoutEl = this.$refs.navFlyout;
      const flyoutHeight = flyoutEl ? flyoutEl.getBoundingClientRect().height : 0;
      const viewportPadding = 8;

      if (!flyoutHeight) {
        this.flyoutTop = Math.max(viewportPadding, rect.top);
        if (allowRetry) {
          requestAnimationFrame(() => this.updateFlyoutPosition(group, false));
        }
        return;
      }

      const top = triggerCenterY - flyoutHeight / 2;
      const maxTop = window.innerHeight - flyoutHeight - viewportPadding;
      this.flyoutTop = Math.max(viewportPadding, Math.min(top, maxTop));
    },
    scrollGroupIntoView(group) {
      const navEl = this.$el.querySelector('.sidebar-nav');
      const groupEl = this.$el.querySelector(`[data-sidebar-group="${group}"]`);
      if (!navEl || !groupEl) {
        return;
      }

      const navRect = navEl.getBoundingClientRect();

      // Compact accordion: keep the parent toggle visible so long menus don't scroll it away
      if (this.isCompactSidebar) {
        const toggleEl = groupEl.querySelector('.sidebar-item-toggle');
        if (!toggleEl) {
          return;
        }

        const toggleRect = toggleEl.getBoundingClientRect();
        const submenuEl = groupEl.querySelector('.sidebar-submenu');
        const submenuHeight = submenuEl ? submenuEl.getBoundingClientRect().height : 0;
        const padding = 8;
        const neededBottom = toggleRect.bottom + submenuHeight + padding;

        if (toggleRect.top < navRect.top + padding) {
          navEl.scrollTop -= navRect.top - toggleRect.top + padding;
          return;
        }

        if (neededBottom > navRect.bottom) {
          const overflow = neededBottom - navRect.bottom;
          const maxScrollWithoutHidingToggle = Math.max(0, toggleRect.top - navRect.top - padding);
          navEl.scrollTop += Math.min(overflow, maxScrollWithoutHidingToggle);
        }
        return;
      }

      const groupRect = groupEl.getBoundingClientRect();

      if (groupRect.bottom > navRect.bottom) {
        navEl.scrollTop += groupRect.bottom - navRect.bottom + 8;
      } else if (groupRect.top < navRect.top) {
        navEl.scrollTop -= navRect.top - groupRect.top + 8;
      }
    },
    toggleProfileMenu() {
      this.profileMenuOpen = !this.profileMenuOpen;
      if (this.profileMenuOpen) {
        this.activeFlyout = null;
        this.$nextTick(() => this.updateProfileFlyoutPosition());
      }
    },
    updateProfileFlyoutPosition() {
      const sidebarEl = this.$el;
      const triggerEl = this.$el.querySelector('.sidebar-profile-trigger-btn');
      if (!sidebarEl || !triggerEl) {
        return;
      }

      const sidebarRect = sidebarEl.getBoundingClientRect();
      const triggerRect = triggerEl.getBoundingClientRect();
      this.profileFlyoutLeft = sidebarRect.right + 12;
      this.profileFlyoutBottom = Math.max(16, window.innerHeight - triggerRect.bottom);
    },
    onWindowResize() {
      if (this.profileMenuOpen) {
        this.updateProfileFlyoutPosition();
      }
      if (this.activeFlyout && !this.isCompactSidebar) {
        this.updateFlyoutPosition(this.activeFlyout);
      }
    },
    closeFlyout() {
      this.activeFlyout = null;
      this.profileMenuOpen = false;
      this.$emit('navigate');
    },
    onFlyoutMouseLeave() {
      if (this.isCompactSidebar) {
        return;
      }
      this.activeFlyout = null;
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.activeFlyout = null;
        this.profileMenuOpen = false;
      }
    }
  }
};
</script>

<style scoped>
.sidebar {
  --sidebar-width: 200px;
  --sidebar-collapse-duration: 0.15s;
  --sidebar-primary: #2d64b8;
  --sidebar-primary-soft: #f3f8ff;
  --sidebar-text: #000;
  --sidebar-text-strong: #000;
  --sidebar-muted: #808080;
  --sidebar-icon-muted: #808080;

  position: sticky;
  top: 0;
  z-index: 1040;
  width: var(--sidebar-width);
  height: 100vh;
  display: flex;
  flex-direction: column;
  flex: 0 0 var(--sidebar-width);
  border-right: 1px solid #e8edf4;
  background: #fff;
  color: var(--sidebar-text);
  font-family: Inter, 'Segoe UI', system-ui, -apple-system, sans-serif;
}

.sidebar-header {
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  width: 200px;
  height: 64px;
  padding: 0 8px;
  min-height: 64px;
  background: #fff;
  border-bottom: 1px solid #e8edf4;
  box-sizing: border-box;
  overflow: visible;
}

.sidebar-header-brand-slot {
  position: relative;
  display: flex;
  align-items: center;
  width: 184px;
  min-height: 44px;
}

.sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 184px;
  max-width: none;
  min-height: 44px;
  padding: 0;
  color: inherit;
  text-decoration: none;
  white-space: nowrap;
}

.sidebar-brand-spencers {
  width: 102px;
  height: auto;
  display: block;
  flex: 0 0 102px;
  object-fit: contain;
}

.sidebar-brand-nature-basket {
  width: 72px;
  height: auto;
  display: block;
  flex: 0 0 72px;
  object-fit: contain;
}

.sidebar-collapse-hit-area {
  position: absolute;
  top: 68px;
  left: calc(var(--sidebar-width) - 22px);
  z-index: 4;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  transition: left var(--sidebar-collapse-duration) ease;
}

.sidebar-collapse-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 1px solid #d5e5fb;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 8px rgba(45, 100, 184, 0.12);
  cursor: pointer;
  transition: background 0.15s ease, box-shadow 0.15s ease;
}

.sidebar-collapse-btn:hover,
.sidebar-collapse-btn:focus-visible {
  background: #f3f8ff;
  box-shadow: 0 4px 10px rgba(45, 100, 184, 0.18);
  outline: none;
}

.sidebar-collapse-icon {
  width: 14px;
  height: 14px;
  font-size: 12px;
  line-height: 1;
  display: block;
  color: #808080;
  transition: transform var(--sidebar-collapse-duration) ease;
}

.sidebar-collapse-icon-expanded {
  transform: rotate(180deg);
}

.sidebar-collapsed .sidebar-header {
  width: 200px;
}

.sidebar-collapsed .sidebar-header-brand-slot,
.sidebar-collapsed .sidebar-brand {
  width: 184px;
  max-width: none;
}

.sidebar-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px 12px;
  gap: 0;
  display: flex;
  flex-direction: column;
}

.sidebar-nav > .sidebar-item,
.sidebar-nav > .sidebar-group {
  flex: 0 0 auto;
}

.sidebar-group {
  overflow: hidden;
}

.sidebar-footer {
  flex: 0 0 auto;
  padding: 12px 12px 16px;
  border-top: 1px solid #f0f3f8;
}

.sidebar-profile-trigger-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 176px;
  max-width: 100%;
  height: 40px;
  padding: 0 4px 0 0;
  gap: 8px;
  border: 0;
  border-radius: 0;
  background: transparent;
  color: inherit;
  text-decoration: none;
  box-shadow: none;
  cursor: pointer;
}

.sidebar-profile-trigger-btn:hover,
.sidebar-profile-trigger-btn:focus,
.sidebar-profile-trigger-btn:active,
.sidebar-profile-trigger-btn.is-active {
  background: transparent;
  color: inherit;
  text-decoration: none;
  box-shadow: none;
  outline: none;
}

.sidebar-profile-flyout {
  top: auto;
  max-height: min(420px, calc(100vh - 32px));
}

.sidebar-profile-flyout .sidebar-flyout-item {
  width: 100%;
  cursor: pointer;
}

.sidebar-item,
.sidebar-subitem,
.sidebar-flyout-item {
  position: relative;
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #000;
  font-family: Inter, 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  text-decoration: none;
  transition: background 0.15s ease, color 0.15s ease;
}

.sidebar-subitem {
  min-height: 36px;
  height: auto;
  padding: 0 10px;
  gap: 12px;
  align-items: center;
  font-size: 12px;
  font-weight: 400;
}

.sidebar-subitem .sidebar-item-icon {
  margin-top: 0;
  flex-shrink: 0;
}

.sidebar-subitem .sidebar-item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 16px;
}

.sidebar-item-icon {
  width: 16px;
  flex: 0 0 16px;
  color: var(--sidebar-icon-muted);
  font-size: 14px;
  text-align: center;
}

.sidebar-item-icon-mask {
  height: 16px;
  display: inline-block;
  background-color: var(--sidebar-icon-muted);
}

.sidebar-item-icon-petty-cash {
  -webkit-mask: url('../assets/icons/petty-cash.png') no-repeat center / contain;
  mask: url('../assets/icons/petty-cash.png') no-repeat center / contain;
}

.sidebar-item.is-active .sidebar-item-icon-mask,
.sidebar-item-toggle.is-active .sidebar-item-icon-mask {
  background-color: #2d64b8;
}

.sidebar-item-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-badge {
  flex: 0 0 auto;
  min-width: 16px;
  height: 16px;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  border-radius: 4px;
  background: #f3f8ff;
  color: #454545;
  font-family: Inter, 'Segoe UI', system-ui, -apple-system, sans-serif;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
  text-align: center;
}

.sidebar-chevron {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  font-size: 12px;
  line-height: 1;
  display: block;
  color: var(--sidebar-icon-muted);
  opacity: 1;
  transition: transform var(--sidebar-collapse-duration) ease;
}

.sidebar-item-toggle > .sidebar-chevron:last-child {
  margin-left: auto;
}

.sidebar-item-toggle .sidebar-badge + .sidebar-chevron {
  margin-left: 0;
}

.sidebar-chevron.is-open {
  transform: rotate(-90deg);
}

.sidebar-chevron-right {
  transform: none;
}

.sidebar-chevron-right.is-open {
  transform: rotate(-90deg);
}

.sidebar-item:hover,
.sidebar-subitem:hover,
.sidebar-flyout-item:hover {
  background: #f3f8ff;
  color: #000;
}

.sidebar-item.is-active,
.sidebar-subitem.is-active,
.sidebar-item-toggle.is-active {
  background: #f3f8ff;
  color: #2d64b8;
  font-weight: 700;
}

.sidebar-item.is-active::before,
.sidebar-item-toggle.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 26px;
  border-radius: 0 2px 2px 0;
  background: #2d64b8;
}

.sidebar-flyout-item.is-active {
  background: #fff;
  color: #2d64b8;
  font-weight: 700;
}

.sidebar-flyout-item.is-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 26px;
  border-radius: 0 2px 2px 0;
  background: #2d64b8;
}

.sidebar-item.is-active .sidebar-item-icon,
.sidebar-subitem.is-active .sidebar-item-icon,
.sidebar-flyout-item.is-active .sidebar-item-icon,
.sidebar-item-toggle.is-active .sidebar-item-icon {
  color: #2d64b8;
}

.sidebar-submenu {
  margin: 4px 0 6px;
  padding: 6px;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  background: #fff;
  max-height: min(320px, 45vh);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #2d64b8 transparent;
}

.sidebar-submenu::-webkit-scrollbar {
  width: 4px;
}

.sidebar-submenu::-webkit-scrollbar-track {
  background: transparent;
  margin: 4px 0;
}

.sidebar-submenu::-webkit-scrollbar-thumb {
  background: #2d64b8;
  border-radius: 4px;
}

.sidebar-submenu::-webkit-scrollbar-thumb:hover {
  background: #2456a0;
}

/* More Functions has many items â€” show ~4 rows, scroll the rest */
.sidebar-submenu-more-functions {
  max-height: calc((36px * 4) + 12px);
}

.sidebar-submenu .sidebar-subitem.is-active {
  background: #eef4ff;
  color: #2d64b8;
  font-weight: 700;
}

.sidebar-submenu .sidebar-subitem.is-active .sidebar-item-icon {
  color: #2d64b8;
}

.sidebar-dark .sidebar-submenu {
  border-color: #283548;
  background: #172033;
  scrollbar-color: #5b9fd4 transparent;
}

.sidebar-dark .sidebar-submenu::-webkit-scrollbar-thumb {
  background: #5b9fd4;
}

.sidebar-dark .sidebar-submenu::-webkit-scrollbar-thumb:hover {
  background: #7bb3de;
}

.sidebar-dark .sidebar-submenu .sidebar-subitem.is-active {
  background: #223044;
  color: #5b9fd4;
}

.sidebar-dark .sidebar-submenu .sidebar-subitem.is-active .sidebar-item-icon {
  color: #5b9fd4;
}

.sidebar-avatar {
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  border: 0.5px solid #808080;
  background: linear-gradient(135deg, #f2d7c7, #e8b4a0);
  color: #6b3d28;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 700;
  overflow: hidden;
}

.sidebar-footer-text {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.sidebar-footer-text strong {
  display: block;
  overflow: hidden;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #000;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-footer-text small {
  display: block;
  overflow: hidden;
  font-family: Inter, 'Segoe UI', system-ui, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #808080;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-footer-chevron-wrap {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sidebar-footer-chevron {
  width: 16px;
  height: 16px;
  font-size: 12px;
  line-height: 1;
  display: block;
  color: #808080;
  opacity: 1;
}

/* Flyout */
.sidebar-flyout {
  position: fixed;
  top: 72px;
  left: calc(var(--sidebar-width) + 12px);
  z-index: 1050;
  width: 248px;
  max-height: calc(100vh - 88px);
  display: flex;
  flex-direction: column;
  border: 1px solid #e8edf4;
  border-radius: 14px;
  background: #f8fafd;
  box-shadow: 0 16px 40px rgba(26, 46, 81, 0.12);
  overflow: hidden;
}

.sidebar-flyout-nav {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  background: #f8fafd;
}

.sidebar-flyout-item {
  min-height: 36px;
  font-size: 12px;
  font-weight: 400;
}

.sidebar-flyout-item-icon-img {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  display: block;
  object-fit: contain;
}

.sidebar-flyout-item-icon-nps-customer {
  filter: brightness(0) saturate(100%) invert(56%) sepia(30%) saturate(1200%) hue-rotate(176deg) brightness(105%) contrast(86%);
}

.sidebar-dark .sidebar-flyout-item-icon-nps-customer {
  filter: brightness(0) saturate(100%) invert(72%) sepia(22%) saturate(900%) hue-rotate(176deg) brightness(100%) contrast(88%);
}

.sidebar-flyout-enter-active,
.sidebar-flyout-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.sidebar-flyout-enter,
.sidebar-flyout-leave-to {
  opacity: 0;
  transform: translateX(-6px);
}

.sidebar-collapsed {
  --sidebar-width: 76px;
}

.sidebar-collapsed .sidebar-brand-text,
.sidebar-collapsed .sidebar-body .sidebar-item-label,
.sidebar-collapsed .sidebar-badge,
.sidebar-collapsed .sidebar-chevron,
.sidebar-collapsed .sidebar-footer-text,
.sidebar-collapsed .sidebar-footer-chevron-wrap {
  display: none;
}

.sidebar-collapsed .sidebar-profile-trigger-btn {
  width: 40px;
  padding-right: 0;
  justify-content: center;
}

.sidebar-collapsed .sidebar-item,
.sidebar-collapsed .sidebar-subitem {
  justify-content: center;
  padding: 0;
}

.sidebar-dark {
  --sidebar-primary: #5b9fd4;
  --sidebar-primary-soft: #1a3050;
  --sidebar-text: #c5d0e0;
  --sidebar-text-strong: #f5f7fb;
  --sidebar-muted: #8fa3bc;
  --sidebar-icon-muted: #8fa3bc;

  border-color: #283548;
  background: #172033;
}

.sidebar-dark .sidebar-header {
  background: #172033;
  border-bottom-color: #283548;
}

.sidebar-dark .sidebar-flyout {
  border-color: #283548;
  background: #1a2438;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
}

.sidebar-dark .sidebar-flyout-nav {
  background: #1a2438;
}

.sidebar-dark .sidebar-flyout-item {
  color: #c5d0e0;
}

.sidebar-dark .sidebar-flyout-item .sidebar-item-icon {
  color: #8fa3bc;
}

.sidebar-dark .sidebar-flyout-item:hover,
.sidebar-dark .sidebar-flyout-item:focus {
  background: #1a3050;
  color: #f5f7fb;
}

.sidebar-dark .sidebar-flyout-item:hover .sidebar-item-icon,
.sidebar-dark .sidebar-flyout-item:focus .sidebar-item-icon {
  color: #f5f7fb;
}

.sidebar-dark .sidebar-item.is-active::before,
.sidebar-dark .sidebar-item-toggle.is-active::before {
  background: #5b9fd4;
}

.sidebar-dark .sidebar-flyout-item.is-active {
  background: #223044;
  color: #5b9fd4;
  font-weight: 700;
}

.sidebar-dark .sidebar-flyout-item.is-active::before {
  background: #5b9fd4;
}

.sidebar-dark .sidebar-flyout-item.is-active .sidebar-item-icon {
  color: #5b9fd4;
}

.sidebar-dark .sidebar-profile-flyout {
  background: #172033;
}

.sidebar-dark .sidebar-brand-text strong {
  color: #f5f7fb;
}

.sidebar-dark .sidebar-brand-text small {
  color: #8fa3bc;
}

.sidebar-dark .sidebar-collapse-btn {
  background: #283548;
  border-color: #3d5a80;
}

.sidebar-dark .sidebar-collapse-icon {
  filter: none;
  color: #c5d0e0;
}

.sidebar-dark .sidebar-item,
.sidebar-dark .sidebar-subitem {
  color: #c5d0e0;
}

.sidebar-dark .sidebar-item:hover,
.sidebar-dark .sidebar-subitem:hover {
  background: #1a3050;
  color: #f5f7fb;
}

.sidebar-dark .sidebar-item.is-active,
.sidebar-dark .sidebar-subitem.is-active,
.sidebar-dark .sidebar-item-toggle.is-active {
  background: #1a3050;
  color: #5b9fd4;
}

.sidebar-dark .sidebar-item.is-active .sidebar-item-icon,
.sidebar-dark .sidebar-subitem.is-active .sidebar-item-icon,
.sidebar-dark .sidebar-item-toggle.is-active .sidebar-item-icon {
  color: #5b9fd4;
}

.sidebar-dark .sidebar-item.is-active .sidebar-item-icon-mask,
.sidebar-dark .sidebar-item-toggle.is-active .sidebar-item-icon-mask {
  background-color: #5b9fd4;
}

.sidebar-dark .sidebar-badge {
  background: #1a3050;
  color: #c5d0e0;
}

.sidebar-dark .sidebar-footer {
  border-color: #283548;
}

.sidebar-dark .sidebar-footer-text strong {
  color: #f5f7fb;
}

.sidebar-dark .sidebar-footer-text small {
  color: #8fa3bc;
}

.sidebar-dark .sidebar-avatar {
  border-color: #8fa3bc;
}

@media (max-width: 767.98px) {
  .sidebar {
    position: fixed;
    left: 0;
    top: 0;
    transform: translateX(-100%);
    transition: transform 0.2s ease;
    box-shadow: 0 12px 40px rgba(15, 39, 75, 0.18);
  }

  .sidebar.sidebar-open {
    transform: translateX(0);
  }

  .sidebar-group {
    overflow: visible;
  }

  .sidebar-flyout:not(.sidebar-profile-flyout) {
    display: none;
  }
}
</style>
